import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["track", "container", "prevBtn", "nextBtn", "dots", "counter"]
  static values = { 
    total: Number,
    mobile: { type: Number, default: 1 },
    tablet: { type: Number, default: 2 },
    desktop: { type: Number, default: 3 },
    mobileBreakpoint: { type: Number, default: 768 },
    desktopBreakpoint: { type: Number, default: 1024 },
    autoplay: { type: Boolean, default: false },
    autoplayDelay: { type: Number, default: 5000 },
    infinite: { type: Boolean, default: false },
    // Opt-in. `infinite` alone sends currentIndex from maxIndex straight back to
    // 0, and the CSS transition then ANIMATES that — so every cycle ends by
    // sliding the whole track backwards. Seamless mode carries on forwards into a
    // duplicate of the leading cards, then snaps back with the transition off,
    // which is invisible. Off by default so the other carousels are untouched.
    seamless: { type: Boolean, default: false },
    showDots: { type: Boolean, default: true },
    showArrows: { type: Boolean, default: true },
    slideBy: { type: Number, default: 1 }
  }

  connect() {
    this.currentIndex = 0
    this.autoplayTimer = null
    this.updateCardsPerView()
    if (this.seamlessValue) this.installClones()

    if (this.showDotsValue) this.createDots()
    this.updateCarousel()
    this.setupAutoplay()
    
    // Handle window resize
    this.handleResize = this.handleResize.bind(this)
    window.addEventListener('resize', this.handleResize)
  }

  disconnect() {
    window.removeEventListener('resize', this.handleResize)
    this.clearAutoplay()
  }

  updateCardsPerView() {
    const width = window.innerWidth
    if (width < this.mobileBreakpointValue) {
      this.cardsPerView = this.mobileValue
    } else if (width < this.desktopBreakpointValue) {
      this.cardsPerView = this.tabletValue
    } else {
      this.cardsPerView = this.desktopValue
    }
    this.maxIndex = Math.max(0, this.totalValue - this.cardsPerView)
  }

  // Copy the leading cards onto the end. Advancing past the last real card then
  // moves FORWARD into an identical view, and we reset to 0 behind the visitor's
  // back while the transition is off.
  installClones() {
    if (!this.hasTrackTarget) return

    this.removeClones()
    Array.from(this.trackTarget.children)
      .slice(0, this.cardsPerView)
      .forEach((node) => {
        const clone = node.cloneNode(true)
        clone.dataset.carouselClone = "true"
        clone.setAttribute("aria-hidden", "true")
        this.trackTarget.appendChild(clone)
      })
  }

  removeClones() {
    if (!this.hasTrackTarget) return
    this.trackTarget.querySelectorAll("[data-carousel-clone]").forEach((node) => node.remove())
  }

  // Move without animating — used to cross the loop seam unseen.
  jumpTo(index) {
    this.trackTarget.style.transition = "none"
    this.currentIndex = index
    this.updateCarousel()
    this.trackTarget.offsetHeight // force reflow so the change lands before we restore
    this.trackTarget.style.transition = ""
  }

  setupAutoplay() {
    if (this.autoplayValue) {
      this.startAutoplay()
      // Pause on hover
      if (this.hasContainerTarget) {
        this.containerTarget.addEventListener('mouseenter', () => this.clearAutoplay())
        this.containerTarget.addEventListener('mouseleave', () => this.startAutoplay())
      }
    }
  }

  startAutoplay() {
    this.clearAutoplay()
    this.autoplayTimer = setInterval(() => {
      this.next()
    }, this.autoplayDelayValue)
  }

  clearAutoplay() {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer)
      this.autoplayTimer = null
    }
  }

  createDots() {
    if (!this.hasDotsTarget) return

    const dotsCount = Math.ceil(this.totalValue / this.cardsPerView)
    this.dotsTarget.innerHTML = ''

    for (let i = 0; i < dotsCount; i++) {
      const dot = document.createElement('button')
      dot.className = 'w-6 h-6 rounded-full transition-colors duration-200'
      dot.dataset.action = 'click->carousel#goToSlide'
      dot.dataset.slide = i * this.cardsPerView
      dot.setAttribute('aria-label', `Go to slide ${i + 1}`)
      dot.setAttribute('aria-current', i === 0 ? 'true' : 'false')
      this.dotsTarget.appendChild(dot)
    }
  }

  updateCarousel() {
    if (!this.hasTrackTarget) return
    
    // Handle infinite scroll. Skipped in seamless mode, which needs to reach
    // index === totalValue (the cloned view) before resetting.
    if (!this.seamlessValue) {
      if (this.infiniteValue && this.currentIndex >= this.totalValue) {
        this.currentIndex = 0
      } else if (this.infiniteValue && this.currentIndex < 0) {
        this.currentIndex = this.maxIndex
      }
    }
    
    // Update transform
    const translateX = -(this.currentIndex * (100 / this.cardsPerView))
    this.trackTarget.style.transform = `translateX(${translateX}%)`
    
    // Update button states
    if (this.hasArrowsTarget || (this.hasPrevBtnTarget && this.hasNextBtnTarget)) {
      const atStart = this.currentIndex === 0
      const atEnd = this.currentIndex >= this.maxIndex
      
      if (this.hasPrevBtnTarget) {
        if (this.showArrowsValue) {
          this.prevBtnTarget.style.opacity = (atStart && !this.infiniteValue) ? '0.5' : '1'
          this.prevBtnTarget.disabled = atStart && !this.infiniteValue
        } else {
          this.prevBtnTarget.style.display = 'none'
        }
      }
      
      if (this.hasNextBtnTarget) {
        if (this.showArrowsValue) {
          this.nextBtnTarget.style.opacity = (atEnd && !this.infiniteValue) ? '0.5' : '1'
          this.nextBtnTarget.disabled = atEnd && !this.infiniteValue
        } else {
          this.nextBtnTarget.style.display = 'none'
        }
      }
    }
    
    // Update dots
    if (this.hasDotsTarget && this.showDotsValue) {
      const dots = this.dotsTarget.querySelectorAll('button')
      dots.forEach((dot, index) => {
        const slideIndex = index * this.cardsPerView
        const isActive = slideIndex === this.currentIndex
        dot.className = `w-6 h-6 rounded-full transition-colors duration-200 ${
          isActive ? 'bg-primary-600' : 'bg-gray-300 hover:bg-gray-400'
        }`
        dot.setAttribute('aria-current', isActive ? 'true' : 'false')
      })
    } else if (this.hasDotsTarget) {
      this.dotsTarget.style.display = 'none'
    }

    // Update counter (shows current position)
    if (this.hasCounterTarget) {
      this.counterTarget.textContent = this.currentIndex + 1
    }
  }

  previous() {
    this.clearAutoplay()
    if (this.seamlessValue) {
      // At the start there is nothing to the left, so cross to the duplicate
      // view first — same frame, no transition — and step back out of it.
      if (this.currentIndex <= 0) this.jumpTo(this.totalValue)
      this.currentIndex -= this.slideByValue
      this.updateCarousel()
      if (this.autoplayValue) this.startAutoplay()
      return
    }
    if (this.infiniteValue) {
      this.currentIndex = this.currentIndex <= 0 ? this.maxIndex : Math.max(0, this.currentIndex - this.slideByValue)
    } else if (this.currentIndex > 0) {
      this.currentIndex = Math.max(0, this.currentIndex - this.slideByValue)
    }
    this.updateCarousel()
    if (this.autoplayValue) this.startAutoplay()
  }

  next() {
    this.clearAutoplay()
    if (this.seamlessValue) {
      this.currentIndex += this.slideByValue
      this.updateCarousel()
      // Landed on the cloned view: let the step finish, then reset to the real
      // start with the transition suppressed. Identical frame, so nothing shows.
      if (this.currentIndex >= this.totalValue) {
        this.trackTarget.addEventListener("transitionend", () => this.jumpTo(0), { once: true })
      }
      if (this.autoplayValue) this.startAutoplay()
      return
    }
    if (this.infiniteValue) {
      this.currentIndex = this.currentIndex >= this.maxIndex ? 0 : Math.min(this.maxIndex, this.currentIndex + this.slideByValue)
    } else if (this.currentIndex < this.maxIndex) {
      this.currentIndex = Math.min(this.maxIndex, this.currentIndex + this.slideByValue)
    }
    this.updateCarousel()
    if (this.autoplayValue) this.startAutoplay()
  }

  goToSlide(event) {
    const slideIndex = parseInt(event.target.dataset.slide)
    this.currentIndex = Math.min(slideIndex, this.maxIndex)
    this.updateCarousel()
  }

  handleResize() {
    const oldCardsPerView = this.cardsPerView
    this.updateCardsPerView()
    
    if (oldCardsPerView !== this.cardsPerView) {
      // Adjust current index to stay roughly in the same position
      this.currentIndex = Math.min(this.currentIndex, this.maxIndex)
      // The clone count follows cardsPerView, so a breakpoint change restocks it.
      if (this.seamlessValue) this.installClones()
      if (this.showDotsValue) this.createDots()
      this.updateCarousel()
    }
  }

  // Public API for external control
  goTo(index) {
    this.currentIndex = Math.min(Math.max(0, index), this.maxIndex)
    this.updateCarousel()
  }

  pause() {
    this.clearAutoplay()
  }

  play() {
    if (this.autoplayValue) this.startAutoplay()
  }

  getCurrentIndex() {
    return this.currentIndex
  }

  getTotalSlides() {
    return this.totalValue
  }
}